import type { ReactNode } from 'react';

import { cx } from '@mage-ui/styled-system/css';
import {
  calendarControl,
  calendarDay,
  calendarHeader,
  calendarHeaderControlIcon,
  calendarHeaderLevel,
  calendarMonth,
  calendarMonthRow,
  calendarMonthsList,
  calendarMonthsListCell,
  calendarMonthsListControl,
  calendarMonthsListRow,
  calendarWeekDay,
  calendarWeekDaysRow,
  calendarYearsList,
  calendarYearsListCell,
  calendarYearsListControl,
  calendarYearsListRow,
  datepickerDropdown,
  datepickerInput,
  input,
  inputDescription,
  inputError,
  inputLabel,
  inputPlaceholder,
  inputRoot,
  inputSection,
  inputWrapper,
} from '@mage-ui/styled-system/recipes';
import { DatePickerInput, type DatePickerValue } from '@mantine/dates';

export type DatePickerProps = {
  name?: string;
  label?: string;
  placeholder?: string;
  description?: string;
  icon?: React.ReactNode;
  error?: string;
  classNames?: DatePickerClassNames;
  value?: DatePickerValue;
  locale?: string;
  maxDate?: Date;
  minDate?: Date;
  format?: string;
  required?: boolean;
  valueFormat?: string;
  onChange?: (date: Date | string | null) => void;
};

export const DatePicker = ({
  classNames,
  label,
  placeholder,
  description,
  icon,
  ...props
}: DatePickerProps): ReactNode => {
  return (
    <DatePickerInput
      label={label ?? 'Pick date'}
      placeholder={placeholder ?? 'Pick date'}
      description={description ?? 'Select a date'}
      classNames={datePickerClassNames(classNames)}
      popoverProps={{
        classNames: popoverClassNames(classNames),
      }}
      rightSection={icon}
      {...props}
    />
  );
};

type DatePickerClassNames = InputClassNamesType &
  CalenderHeaderClassNamesType &
  CalendarMonthClassNamesType &
  CalendarMonthsListClassNamesType &
  CalendarYearsListClassNamesType &
  PopoverClassNamesType;

type InputClassNamesType = {
  root?: string;
  wrapper?: string;
  input?: string;
  label?: string;
  error?: string;
  description?: string;
  placeholder?: string;
  section?: string;
};

const inputClassNames = (classNames?: InputClassNamesType) => {
  return {
    root: classNames?.root ?? inputRoot(),
    wrapper: classNames?.wrapper ?? inputWrapper(),
    input: classNames?.input ?? cx(input(), datepickerInput()),
    label: classNames?.label ?? inputLabel(),
    error: classNames?.error ?? inputError(),
    description: classNames?.description ?? inputDescription(),
    placeholder: classNames?.placeholder ?? inputPlaceholder(),
    section: classNames?.section ?? inputSection({ pointerEvents: 'none' }),
  };
};

type CalenderHeaderClassNamesType = {
  datepickerDropdown?: string;
  calendarHeaderControl?: string;
  calendarHeader?: string;
  calendarHeaderLevel?: string;
  calendarHeaderControlIcon?: string;
};

const calendarHeaderClassNames = (
  classNames?: CalenderHeaderClassNamesType,
) => {
  return {
    calendarHeaderControl:
      classNames?.calendarHeaderControl ?? calendarControl(),
    calendarHeader: classNames?.calendarHeader ?? calendarHeader(),
    calendarHeaderLevel:
      classNames?.calendarHeaderLevel ?? calendarHeaderLevel(),
    calendarHeaderControlIcon:
      classNames?.calendarHeaderControlIcon ?? calendarHeaderControlIcon(),
  };
};

type CalendarMonthClassNamesType = {
  calendarMonth?: string;
  calendarWeekDaysRow?: string;
  calendarWeekDay?: string;
  calendarMonthRow?: string;
  calendarMonthCell?: string;
  calendarDay?: string;
};

const calendarMonthClassNames = (classNames?: CalendarMonthClassNamesType) => {
  return {
    month: classNames?.calendarMonth ?? calendarMonth(),
    weekdaysRow: classNames?.calendarWeekDaysRow ?? calendarWeekDaysRow(),
    weekday: classNames?.calendarWeekDay ?? calendarWeekDay(),
    monthRow: classNames?.calendarMonthRow ?? calendarMonthRow(),
    day: classNames?.calendarDay ?? calendarDay(),
  };
};

type CalendarMonthsListClassNamesType = {
  calendarMonthsList?: string;
  calendarMonthsListRow?: string;
  calendarMonthsListCell?: string;
  calendarMonthsListControl?: string;
};

const calendarMonthsListClassNames = (
  classNames?: CalendarMonthsListClassNamesType,
) => {
  return {
    monthsList: classNames?.calendarMonthsList ?? calendarMonthsList(),
    monthsListRow: classNames?.calendarMonthsListRow ?? calendarMonthsListRow(),
    monthsListCell:
      classNames?.calendarMonthsListCell ?? calendarMonthsListCell(),
    monthsListControl:
      classNames?.calendarMonthsListControl ?? calendarMonthsListControl(),
  };
};

type CalendarYearsListClassNamesType = {
  calendarYearsList?: string;
  calendarYearsListRow?: string;
  calendarYearsListCell?: string;
  calendarYearsListControl?: string;
};

const calendarYearsListClassNames = (
  classNames?: CalendarYearsListClassNamesType,
) => {
  return {
    yearsList: classNames?.calendarYearsList ?? calendarYearsList(),
    yearsListRow: classNames?.calendarYearsListRow ?? calendarYearsListRow(),
    yearsListCell: classNames?.calendarYearsListCell ?? calendarYearsListCell(),
    yearsListControl:
      classNames?.calendarYearsListControl ?? calendarYearsListControl(),
  };
};

type PopoverClassNamesType = {
  datepickerDropdown?: string;
};

const popoverClassNames = (classNames?: PopoverClassNamesType) => {
  return {
    dropdown: classNames?.datepickerDropdown ?? datepickerDropdown(),
  };
};

const datePickerClassNames = (classNames?: DatePickerClassNames) => {
  return {
    ...inputClassNames(classNames),
    ...calendarHeaderClassNames(classNames),
    ...calendarMonthClassNames(classNames),
    ...calendarMonthsListClassNames(classNames),
    ...calendarYearsListClassNames(classNames),
  };
};
